import type { ReactNode } from 'react'
import { useCoachingStore } from '../../store'
import { AssignmentActions } from './assignment-actions'

type InterviewTier = 'ai' | 'peer' | 'coach'

interface TierCard {
  id: InterviewTier
  icon: string
  name: string
  step: string
  color: string
  background: string
  border: string
  description: string
  link: string
  linkLabel: string
  est: number
}

const TIERS: TierCard[] = [
  {
    id: 'ai',
    icon: '🤖',
    name: 'AI mock interview',
    step: 'STEP 1 · LOWEST FRICTION',
    color: 'var(--cd-green)',
    background: 'color-mix(in oklab, var(--cd-green) 12%, transparent)',
    border: 'color-mix(in oklab, var(--cd-green) 25%, transparent)',
    description:
      'The only day-specific one: drop it on a day, then link it to any question — Jordan sees just "AI mock interview", never the title. You can also convert any unfinished card with its 🤖 button.',
    link: '#ai-interviewer',
    linkLabel: 'open AI interviewer',
    est: 1,
  },
  {
    id: 'peer',
    icon: '👥',
    name: 'Peer mocks — scheduling task',
    step: 'STEP 2 · REAL PRESSURE',
    color: 'var(--cd-amber)',
    background: 'color-mix(in oklab, var(--cd-amber) 12%, transparent)',
    border: 'color-mix(in oklab, var(--cd-amber) 25%, transparent)',
    description:
      'Creates "Schedule 2 peer mocks" — a task telling Jordan to book the mocks, not the booking itself. Completion is detected automatically when the bookings appear on their calendar.',
    link: '#peer-scheduling',
    linkLabel: 'peer scheduling',
    est: 0.5,
  },
  {
    id: 'coach',
    icon: '🎓',
    name: 'Coach session — scheduling task',
    step: 'STEP 3 · HIGHEST SIGNAL',
    color: 'var(--cd-purple)',
    background: 'color-mix(in oklab, var(--cd-purple) 12%, transparent)',
    border: 'color-mix(in oklab, var(--cd-purple) 25%, transparent)',
    description:
      'A task to book a session with you — the session itself lands on the board as an immovable 📅 calendar event once scheduled.',
    link: '#coach-booking',
    linkLabel: 'your booking page',
    est: 0.5,
  },
]

const PERSISTED_DESCRIPTIONS: Record<InterviewTier, string> = {
  ai: 'The only day-specific one: drop it on a day, then link it to any question — the client sees just "AI mock interview", never the title. You can also convert any unfinished card with its 🤖 button.',
  peer: 'Creates "Schedule 2 peer mocks" — a task telling the client to book the mocks, not the booking itself. Record completion manually after the client books them.',
  coach:
    'Creates a task telling the client to book a session with you. Record completion manually after they book; any scheduled calendar event appears separately.',
}

export function InterviewTab(): ReactNode {
  const { curriculum } = useCoachingStore()

  return (
    <div className="space-y-2 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
      <p className="text-[10.5px] text-(--cd-text-faint) xl:shrink-0">
        A sliding scale of interview practice — the AI is the lowest-friction
        rung, a coach session the highest-signal one.
      </p>
      {/* The only tab body with no scroller of its own — uncapped it was
          always as tall as its five tiers, which the row splitter can now
          make taller than the card. */}
      <div className="flex flex-col gap-2 xl:min-h-0 xl:flex-1 xl:overflow-y-auto">
        {TIERS.map((tier) => (
          <article
            key={tier.id}
            aria-label={tier.name}
            className="rounded-xl border bg-(--cd-inset) px-3 py-2.5"
            style={{ borderColor: tier.border }}
          >
            <div className="flex flex-wrap items-center gap-2.5">
              <span aria-hidden="true" className="shrink-0 text-base">
                {tier.icon}
              </span>
              <div className="min-w-[200px] flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-xs font-bold text-(--cd-text)">
                    {tier.name}
                  </h3>
                  <span
                    className="rounded-full px-2 py-0.5 text-[8.5px] font-bold tracking-[0.06em]"
                    style={{ color: tier.color, background: tier.background }}
                  >
                    {tier.step}
                  </span>
                  <span className="text-[9.5px] text-(--cd-text-faint)">
                    ~{tier.est}h
                  </span>
                </div>
                <p className="mt-0.5 text-[10.5px] leading-4 text-(--cd-text-muted)">
                  {curriculum.persisted
                    ? PERSISTED_DESCRIPTIONS[tier.id]
                    : tier.description}
                </p>
              </div>
              {!curriculum.persisted ? (
                <a
                  href={tier.link}
                  className="whitespace-nowrap text-[10px] font-semibold text-(--cd-text) underline-offset-2 hover:underline"
                >
                  🔗 {tier.linkLabel}
                </a>
              ) : null}
              <AssignmentActions
                variant="standard"
                disabled={curriculum.stagePending}
                addLabel={`Add ${tier.name}`}
                trayLabel={`Tray ${tier.name}`}
                onAdd={() => curriculum.addInterviewTier(tier.id, 'auto')}
                onTray={() => curriculum.addInterviewTier(tier.id, 'tray')}
              />
            </div>
          </article>
        ))}
      </div>
      <p className="text-[10px] leading-4 text-(--cd-text-faint)">
        Rule of thumb: AI mocks weekly, a peer mock before each real phone
        screen, a coach session before each onsite loop.
      </p>
    </div>
  )
}
